import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useSettings } from '../hooks/useSettings';
import { format, parse } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

interface SettingsContextType {
  settings: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    smsNotifications: boolean;
    marketingEmails: boolean;
    invoiceReminders: boolean;
    serviceAlerts: boolean;
    autoSave: boolean;
    darkMode: boolean;
    timezone: string;
    dateFormat: string;
    currency: string;
    language: string;
    twoFactorAuth: boolean;
  };
  loading: boolean;
  formatDate: (date: Date | string, includeTime?: boolean) => string;
  formatCurrency: (amount: number) => string;
  convertToUserTimezone: (date: Date | string) => Date;
  formatInUserTimezone: (date: Date | string, formatStr: string) => string;
  updateSetting: (key: string, value: any) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { settings, loading, updateSetting: updateSettingHook } = useSettings();
  
  // Currency symbols
  const currencySymbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£'
  };

  // Date format patterns
  const dateFormatPatterns: Record<string, string> = {
    'DD/MM/YYYY': 'dd/MM/yyyy',
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd'
  };

  // Format date according to user settings
  const formatDate = (date: Date | string, includeTime: boolean = false): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const locale = settings.language === 'es' ? es : enUS;
      const pattern = dateFormatPatterns[settings.dateFormat] || 'dd/MM/yyyy';
      const fullPattern = includeTime ? `${pattern} HH:mm` : pattern;
      
      return format(dateObj, fullPattern, { locale });
    } catch (error) {
      console.error('Error formatting date:', error);
      return String(date);
    }
  };

  // Format currency according to user settings
  const formatCurrency = (amount: number): string => {
    try {
      const formatter = new Intl.NumberFormat(settings.language === 'es' ? 'es-ES' : 'en-US', {
        style: 'currency',
        currency: settings.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return formatter.format(amount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `${currencySymbols[settings.currency] || '€'}${amount.toFixed(2)}`;
    }
  };

  // Convert date to user's timezone
  const convertToUserTimezone = (date: Date | string): Date => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return toZonedTime(dateObj, settings.timezone);
    } catch (error) {
      console.error('Error converting timezone:', error);
      return typeof date === 'string' ? new Date(date) : date;
    }
  };

  // Format date in user's timezone
  const formatInUserTimezone = (date: Date | string, formatStr: string): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return formatInTimeZone(dateObj, settings.timezone, formatStr);
    } catch (error) {
      console.error('Error formatting in timezone:', error);
      return String(date);
    }
  };

  // Wrapper for updateSetting to handle any additional logic
  const updateSetting = async (key: string, value: any) => {
    await updateSettingHook(key as any, value);
  };

  const value: SettingsContextType = {
    settings,
    loading,
    formatDate,
    formatCurrency,
    convertToUserTimezone,
    formatInUserTimezone,
    updateSetting
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
};